import { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  FormHelperText,
  InputAdornment,
  Button,
  makeStyles,
  Typography,
} from "@material-ui/core";
import {
  AddCircleOutline as Unverified,
  CheckCircle as Check,
} from "@material-ui/icons";
import AvatarPhotoField from "../UI/Fields/AvatarPhotoField";
import { PageLoading } from "components/UI/Page";
import { ProfileStore, UserStore } from "../../store/index";

import { lang } from "../../i18nLang";

const useStyles = makeStyles((theme) => ({
  formHelperTextError: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
  formHelperLink: {
    fontWeight: 500,
    cursor: "pointer",
    textDecoration: "underline",
  },
  unverifiedIcon: {
    transform: "rotate(45deg)",
    fontSize: "22px",
    color: theme.palette.error.main,
  },
  checkIcon: {
    fontSize: "22px",
    color: theme.palette.success.main,
  },
  loading_card: {
    position: "absolute",
    top: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(255,255,255,0.8)",
    zIndex: 1,
    marginLeft: "-0.5rem",
  },
}));

const MyProfile = () => {
  const classes = useStyles();
  const [data, setData] = useState({
    name: "",
    email: "",
    photoUrl: "",
    emailVerified: false,
  });
  const [enableSave, setEnableSave] = useState(false);
  const [userPhoto, setUserPhoto] = useState<File>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const user = UserStore.currentUser;
    if (user) {
      setData({
        name: user.name,
        email: user.email,
        photoUrl: user.photoURL || "",
        emailVerified: user.emailVerified || false,
      });
      setUserPhoto(UserStore.userPhoto);
    }
  }, []);

  const handleChangeInput = (ev) => {
    const { name, value } = ev.target;
    const newData = { ...data };
    newData[name] = value;
    setData(newData);
    if (!enableSave) setEnableSave(true);
  };

  const handleClickSave = () => {
    const userData = { name: data.name, email: data.email };

    setLoading(true);
    ProfileStore.updateProfile(userData, userPhoto)
      .then(() => {
        changeEmailVerified();
        setEnableSave(false);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changeEmailVerified = () => {
    const user = UserStore.currentUser;
    handleChangeInput({
      target: { name: "emailVerified", value: user?.emailVerified || false },
    });
  };

  const handleChangePhoto = (file: File) => {
    setUserPhoto(file);
    if (!enableSave) setEnableSave(true);
  };

  const handleClickReset = () => {
    setLoading(true);
    ProfileStore.resetPassword().finally(() => setLoading(false));
  };

  const handleClickVerification = () => {
    setLoading(true);
    ProfileStore.sendVerificationEmail().finally(() => setLoading(false));
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      style={{ padding: "4rem 0" }}
    >
      {loading && (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          className={classes.loading_card}
        >
          <PageLoading />
        </Grid>
      )}
      <Grid container item direction="row" xs={8} lg={6}>
        <Grid
          container
          item
          xs={2}
          justifyContent="center"
          alignItems="flex-start"
        >
          <AvatarPhotoField file={userPhoto} onChange={handleChangePhoto} />
        </Grid>
        <Grid container item xs direction="column">
          <TextField
            name="name"
            value={data.name}
            label={lang("fields.name")}
            onChange={handleChangeInput}
            fullWidth
            inputProps={{
              maxLength: "30",
            }}
          />
          <TextField
            name="email"
            value={data.email}
            label={lang("fields.email")}
            onChange={handleChangeInput}
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {data.emailVerified ? (
                    <Check className={classes.checkIcon} />
                  ) : (
                    <Unverified className={classes.unverifiedIcon} />
                  )}
                </InputAdornment>
              ),
            }}
          />
          {!data.emailVerified && (
            <FormHelperText
              id="component-text"
              className={classes.formHelperTextError}
            >
              {"E-mail não verificado. "}
              <Typography
                variant="caption"
                className={classes.formHelperLink}
                onClick={handleClickVerification}
              >
                {"Enviar verificação novamente"}
              </Typography>
            </FormHelperText>
          )}
          <Grid
            container
            justifyContent="space-between"
            style={{ padding: "3rem 0" }}
          >
            <Button
              variant="outlined"
              color="primary"
              disabled={loading}
              onClick={handleClickReset}
            >
              {lang("resetPassword")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickSave}
              disabled={loading || !enableSave}
              style={{ float: "right" }}
            >
              {lang("save")}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default MyProfile;
